import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { PageBase } from '../PageBase';
import TextField from '@/components/textField/TextField';
import Button from '@/components/button/Button';
import { theme } from '@/styles/theme';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserService } from '@/data/services/useUserService';
import { useAuthForgotPasswordStore } from '../createNewPassword/store/auth';
import WarningField from '@/components/warningField/WarningField';
import { useTranslation } from 'react-i18next';
import { AppError } from '@/utils/AppError';

interface ForgotPasswordInputs {
  email: string;
}

export const ForgotPassword = () => {
  const { t } = useTranslation('forgot_password');

  const { setEmail } = useAuthForgotPasswordStore();
  const { recoveryPassword } = useUserService();
  const [loading, setLoading] = useState(false);

  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const location = useLocation();
  const { locationEmail } = location.state || {};

  const handleWarningRightButtonAction = () => {
    setShowWarning(false);
  };
  const navigate = useNavigate();

  const schema = yup.object().shape({
    email: yup.string().email(t('email_invalid')).required(t('email_required')),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordInputs>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: ForgotPasswordInputs) => {
    if (locationEmail == undefined) {
      sendRecoveryPassword(data);
    } else {
      sendResendToken();
    }
  };

  const sendRecoveryPassword = (data: ForgotPasswordInputs) => {
    setLoading(true);
    recoveryPassword(data.email)
      .then(() => {
        setEmail(data.email);
        navigate('/create-new-password');
      })
      .catch((_error: AppError) => {
        setShowWarning(true);
        setWarningMessage(t('error_sending_email'));
      })
      .finally(() => setLoading(false));
  };

  const sendResendToken = () => {
    navigate('/resend_code', {
      state: { locationEmail },
    });
  };

  return (
    <PageBase
      title={t(
        locationEmail ? 'account_validation_title' : 'forgot_password_title'
      )}
      onBackClick={() => {
        location.state = {};
        navigate(-1);
      }}
      paddingInline={0}
      sxContainer={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: theme.spacing(2),
      }}
      sxContentBox={{
        backgroundColor: theme.palette.secondary.contrastText,
        borderRadius: 2,
        boxShadow: 1,
        paddingInline: 4,
        marginTop: 4,
        paddingBlock: 4,
        width: 550,
      }}
    >
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
          gap: 4,
        }}
      >
        <Box
          sx={{
            [theme.breakpoints.down('sm')]: {
              width: 280,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '1.8rem',
              fontWeight: 600,
              color: theme.palette.primary.main,
              [theme.breakpoints.down('sm')]: {
                fontSize: '1.5rem',
              },
            }}
          >
            {t(
              locationEmail
                ? 'forgot_password_subtitle'
                : 'forgot_password_subtitle'
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontWeight: 400,
              [theme.breakpoints.down('sm')]: {
                fontSize: '1.2rem',
              },
            }}
          >
            {t(
              locationEmail
                ? 'account_validation_description'
                : 'forgot_description'
            )}
          </Typography>
        </Box>

        <Controller
          name="email"
          control={control}
          defaultValue={locationEmail ?? ''}
          disabled={locationEmail != undefined}
          render={({ field }) => (
            <TextField
              {...field}
              title="E-mail"
              error={!!errors.email}
              helperText={errors.email?.message}
              placeholder={t('email_placeholder')}
              onValueChange={(value) => field.onChange(value)}
              sx={{
                [theme.breakpoints.down('sm')]: {
                  width: 260,
                },
              }}
            />
          )}
        />

        <WarningField
          description={warningMessage}
          isHidden={!showWarning}
          setShowWarning={setShowWarning}
          status="warning"
          rightButtonTitle={t('close')}
          rightButtonAction={handleWarningRightButtonAction}
        />

        <Button
          title={t('btn_email_sent')}
          onClick={handleSubmit(onSubmit)}
          disable={!isValid || loading}
          loading={loading}
          sx={{
            [theme.breakpoints.down('sm')]: {
              width: 300,
            },
          }}
        />
      </Box>
    </PageBase>
  );
};
